/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useEffect, useState } from "react";

import { ViewModel } from "../../Common/ViewModel";
import { Panel } from "../../../../ui/components/Panel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import {
	ISchemaComponentModel,
	ISchemaDataSourceModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap,
	IScope,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { ScopeViewer } from "../../../../ui/components/ScopeViewer/ScopeViewer";
import { BEM, useTranslate } from "@adapptio/lib-components";

function resolveScope(model: ViewModel): {
	scope: IScope;
	model?: TGenericModelNode;
	id?: string;
	type?: string;
} {
	const selectedItems = model.getSelection().getItems();
	const eventNodes = model.getSelection().getEventNodes();

	const globalScope = {
		scope: model.getRuntimeContext().getLastScope(),
		model: null,
		id: null
	};

	if (selectedItems.length === 0) {
		return globalScope;
	} else if (eventNodes.length === 1) {
		const node = model
			.getDesignContext()
			.getNodeById(eventNodes[0].nodeId) as ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>;

		if (node?.executionResult?.scope) {
			return {
				scope: node.executionResult?.scope,
				model: node,
				id: node.varName.value,
				type: node.schema.opts.nodeTypes[node.type].label
			};
		} else {
			let nodeId;
			let parentNode = node?.parent;

			while (parentNode) {
				nodeId = parentNode.nodeId;
				parentNode = parentNode.parent;
			}

			if (nodeId) {
				const parentScope = model.getDesignContext().getNodeById(nodeId);

				return {
					scope: parentScope.lastScopeFromRender,
					model: parentScope,
					id:
						parentScope.schema.name === "component"
							? (parentScope as ISchemaComponentModel).id.value
							: null,
					type: "Component"
				};
			} else {
				return globalScope;
			}
		}
	} else if (selectedItems.length === 1) {
		const node = model.getDesignContext().getNodeById(selectedItems[0].nodeId);

		if (node?.lastScopeFromRender) {
			return {
				scope: node.lastScopeFromRender,
				model: node,
				id:
					node.schema.name === "component"
						? (node as ISchemaComponentModel).id.value
						: node.schema.name === "dataSource"
						? (node as ISchemaDataSourceModel).id.value
						: null
			};
		} else {
			return globalScope;
		}
	} else {
		return globalScope;
	}
}

/*
 * Toolbar
 */

export interface IViewEditorDataExplorerToolbarProps {}

export const ViewEditorDataExplorerToolbar: FC<IViewEditorDataExplorerToolbarProps> = () => {
	// @todo translate
	return (
		<>
			<ToolbarTitle>Data Explorer</ToolbarTitle>
		</>
	);
};

/*
 * Content
 */

export interface IViewEditorDataExplorerContentProps {
	model: ViewModel;
}

export const ViewEditorDataExplorerContent: FC<IViewEditorDataExplorerContentProps> = ({ model }) => {
	const t = useTranslate();

	const [ scope, setScope ] = useState(() => resolveScope(model));
	const [ lastInfo, setLastInfo ] = useState(() => ({
		id: scope.id,
		type: scope.type
	}));

	useEffect(() => {
		const editSelection = model.getSelection();

		const handleUpdate = () => setScope(resolveScope(model));

		onEvent(editSelection.onChange, handleUpdate);
		onEvent(editSelection.onEventChange, handleUpdate);
		onEvent(editSelection.onEventNodesChange, handleUpdate);
		onEvent(model.onRender, handleUpdate);

		return () => {
			offEvent(editSelection.onChange, handleUpdate);
			offEvent(editSelection.onEventChange, handleUpdate);
			offEvent(editSelection.onEventNodesChange, handleUpdate);
			offEvent(model.onRender, handleUpdate);
		};
	}, [ model ]);

	useEffect(() => {
		if (scope?.id || scope?.type) {
			setLastInfo({
				id: scope.id,
				type: scope.type
			});
		}
	}, [ scope?.id, scope?.type ]);

	const baseClass = BEM("edt-view-editor-data-explorer");
	const schemaName = scope?.model?.schema.name;

	const showNote = true;
	let noteContent = (
		<>
			Only <strong>global scope</strong> is shown
		</>
	);

	switch (schemaName) {
		case "component": {
			// @todo Translate
			noteContent = (
				<>
					Showing data as seen by component: <strong>{lastInfo.id || ""}</strong>
				</>
			);
			break;
		}
		case "dataSource": {
			// @todo Translate
			noteContent = (
				<>
					Showing data as seen by data source: <strong>{lastInfo.id || ""}</strong>
				</>
			);
			break;
		}
		case "flowNode": {
			// @todo Translate
			noteContent = (
				<>
					Showing data as seen by flow node:{" "}
					<strong>
						{t("editor", lastInfo.type)} {lastInfo.id ? `(${lastInfo.id})` : ""}
					</strong>
				</>
			);
			break;
		}
	}

	return (
		<Panel scroll={false} className={baseClass.toClassName()}>
			<div className={baseClass.element("tree").toClassName()}>
				<ScopeViewer
					sort
					scope={scope?.scope}
					// @todo Translate
					noScopeNote={
						schemaName === "flowNode"
							? "Node hasn't been executed yet."
							: "Component hasn't been rendered yet."
					}
				/>
			</div>
			<div className={baseClass.element("note", { show: showNote }).toClassName()}>
				<div className={baseClass.element("note-inner").toClassName()}>{noteContent}</div>
			</div>
		</Panel>
	);
};
